// FundsAvailableReconciler — the "funds available" reconciliation. This is the
// #1 financial-clarity fix from the audit: finance staff saw different
// "available" numbers and couldn't tell which one governs. (Real case: the
// Office Administrator ran the by-object report, saw a much larger "available",
// but the President's email said ~$24,913 — "nowhere near.") This card walks the
// expenditure budget down to the controlling Funds Available so a finance reader
// can SEE which figure actually gates spending.
//
//   Budget remaining (spending side)      $158,751   dept-656500 object roll-up
//     − Other Anticipated / 000000 misfile (~$111,526)
//   BCEA expenditure-only available        $47,225   fund-wide expenditure objs
//     − budgeted-but-uncollected revenue  (~$22,312)  mostly obj 41120
//   = Funds Available (budgetary control)  $24,913 ← gates spending (headline)
//
// The top lens (bc_balance, scoped to dept 656500 = Dale City) and the BCEA
// expenditure-only figure (dash_finance_available, Oracle fund-wide) are
// DIFFERENT scopes: the ~$111K step is expenditure that hit fund 2101 but was
// filed under cost center 000000 ("DO NOT USE ZEROES"), not Dale City — Oracle's
// fund-wide control counts it, the department object report doesn't. The card
// renders this three-lens bridge whenever the gap is material (it is). A
// two-lens fallback auto-engages only if a future load makes the two scopes
// coincide, so the waterfall never degenerates to a $0 step.
//
// All figures come from /api/finance/available (dash_finance_available for the
// captured pair + driver; the bc_balance object roll-up for the spending-side
// figure). The deltas are COMPUTED — never hardcoded. Lives on the Levy Position
// page only. Renders nothing until all figures load.

import InfoTip from "../shared/InfoTip";
import { asNum, usd0 } from "./charts";
import type { FinanceAvailable } from "../../hooks/useFinance";

interface Props {
  data: FinanceAvailable | null | undefined;
  isLoading?: boolean;
  className?: string;
  /** When set, the figures are a PRIOR fiscal year's closing position — label the
   *  reconciliation so its headline isn't read as today's spendable cash. */
  priorYearFy?: string | null;
}

// One waterfall row: a labeled dollar figure with a one-line plain-English note.
// `kind` controls styling — "level" is a measured figure (bold), "delta" is a
// subtraction step (muted, parenthesized), "headline" is the controlling number.
function Row({
  amount,
  label,
  note,
  kind,
  labelTip,
}: {
  amount: number;
  label: string;
  note: string;
  kind: "level" | "delta" | "headline";
  labelTip?: React.ReactNode;
}) {
  const isDelta = kind === "delta";
  const isHeadline = kind === "headline";
  // Deltas are subtractions — render parenthesized and muted, e.g. ($111,526).
  const amountStr = isDelta ? `(${usd0(Math.abs(amount))})` : usd0(amount);
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-2 ${
        isHeadline ? "rounded-lg bg-dcvfd-accent/10 px-3 -mx-1" : ""
      }`}
    >
      <div className="min-w-0">
        <div
          className={`flex items-center gap-1.5 ${
            isHeadline
              ? "text-sm font-semibold text-theme-primary"
              : isDelta
                ? "text-xs text-theme-muted pl-4"
                : "text-sm font-medium text-theme-secondary"
          }`}
        >
          {isDelta && (
            <span aria-hidden className="-ml-3 mr-0.5">
              −
            </span>
          )}
          <span>{label}</span>
          {labelTip}
          {isHeadline && (
            <span className="ml-1 shrink-0 rounded-full bg-dcvfd-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-dcvfd-accent">
              what we can spend
            </span>
          )}
        </div>
        <p
          className={`mt-0.5 leading-snug text-theme-muted ${
            isDelta ? "text-[11px] pl-4" : "text-xs"
          }`}
        >
          {note}
        </p>
      </div>
      <div
        className={`shrink-0 tabular-nums ${
          isHeadline
            ? "text-2xl font-bold text-theme-primary"
            : isDelta
              ? "text-sm text-theme-muted"
              : "text-lg font-semibold text-theme-primary"
        }`}
      >
        {amountStr}
      </div>
    </div>
  );
}

export default function FundsAvailableReconciler({
  data,
  isLoading,
  className,
  priorYearFy = null,
}: Props) {
  // Need all three lenses to tell the whole story truthfully — until they load,
  // render nothing rather than a partial/misleading waterfall.
  if (
    isLoading ||
    data?.expenditure_budget_remaining == null ||
    data?.expenditure_only_available == null ||
    (data?.snapshot_available == null && data?.all_object_available == null)
  ) {
    return null;
  }

  const spendingSide = asNum(data.expenditure_budget_remaining); // object roll-up
  const bceaAvailable = asNum(data.expenditure_only_available); //  BCEA exp-only
  // SINGLE SOURCE OF TRUTH: lead with the snapshot headline (what Overview, the
  // Levy page and the Board Packet all show), so no two surfaces disagree. The
  // recon table's all_object_available is the fallback when no snapshot exists.
  // Because revenueShortfall is DERIVED (bcea − headline), the waterfall always
  // reconciles down to whatever headline we show — here, the Oracle snapshot.
  const headline = asNum(data.snapshot_available ?? data.all_object_available);
  // Disclose freshness drift: if the recon table is a pull behind the snapshot,
  // the decomposition (expenditure-only) is as-of an earlier date.
  const reconAsOf = data.as_of_date;
  const snapAsOf = data.snapshot_as_of;
  const drift =
    data.tie_out_delta != null && Math.abs(data.tie_out_delta) >= 1
      ? data.tie_out_delta
      : 0;

  // Both deltas COMPUTED from the figures — never hardcoded.
  //   step 1: Other Anticipated / capital adjustments = spending-side − BCEA
  //   step 2: budgeted-but-uncollected revenue        = BCEA − headline
  const otherAnticipated = spendingSide - bceaAvailable; // positive subtraction
  const revenueShortfall = bceaAvailable - headline; // positive subtraction

  // Whether the by-object roll-up and the BCEA expenditure-only figure DIVERGE
  // materially. With the authoritative BCA-XML object load they're the same
  // measure (the "Other Anticipated" adjustment is already inside actuals), so
  // the gap is ~$0 and we render the honest TWO-lens bridge instead of a
  // degenerate waterfall that shows $47,225 twice with a $0 step between. If a
  // future source diverges again (gap material), the full three-lens view
  // returns automatically.
  const gapMaterial =
    otherAnticipated > Math.max(1000, Math.abs(headline) * 0.02);

  const driverDesc = data.driver_desc;
  const driverObject = data.driver_object;
  const revenueNote = driverDesc
    ? `Revenue we budgeted but haven't collected — mostly ${
        driverObject ? `obj ${driverObject} ` : ""
      }"${driverDesc}".`
    : "Revenue we budgeted for but haven't collected yet.";

  return (
    <section
      className={`glass rounded-2xl p-5 ${className ?? ""}`}
      aria-label="How funds available reconciles to the controlling number"
    >
      <h2 className="text-sm font-semibold text-theme-primary uppercase tracking-wider mb-1 inline-flex items-center gap-1">
        Reconciling &ldquo;funds available&rdquo;
        {priorYearFy ? (
          <span className="text-status-amber normal-case tracking-normal">
            — {priorYearFy} closing (prior year)
          </span>
        ) : null}
        <InfoTip term="funds_available" />
      </h2>
      <p className="text-xs text-theme-muted mb-3">
        {gapMaterial ? (
          <>
            The by-object report, the county&rsquo;s BCEA report, and our
            headline each show a different &ldquo;available&rdquo; figure. All
            are correct — they sit at different points in the budgetary-control
            stack. Here is how the gross{" "}
            <span className="tabular-nums">{usd0(spendingSide)}</span> on the
            object report walks down to the{" "}
            <span className="tabular-nums">{usd0(headline)}</span> we can
            actually spend.
          </>
        ) : (
          <>
            The expenditure budget still has{" "}
            <span className="tabular-nums">{usd0(bceaAvailable)}</span> unspent,
            but once we net the revenue we budgeted for and haven&rsquo;t
            collected, the controlling Funds Available is{" "}
            <span className="tabular-nums">{usd0(headline)}</span> — the number
            that actually gates spending and ties to Oracle.
          </>
        )}
      </p>

      <div className="border-t border-theme-subtle/50">
        {gapMaterial && (
          <>
            <Row
              kind="level"
              amount={spendingSide}
              label="Budget remaining (spending side)"
              note="Budget − spent − open POs, summed over objects — what the by-object report sums to."
            />
            <Row
              kind="delta"
              amount={otherAnticipated}
              label="Other Anticipated / capital adjustments"
              note="Anticipated expenditures and capital set-asides the object report doesn't subtract."
            />
          </>
        )}
        <Row
          kind="level"
          amount={bceaAvailable}
          label={
            gapMaterial
              ? "BCEA expenditure-only available"
              : "Expenditure budget available"
          }
          labelTip={<InfoTip term="bcea" />}
          note="Budget − spent − open POs over expenditure objects (Mobius BCEA), before the revenue shortfall is netted in."
        />
        <Row
          kind="delta"
          amount={revenueShortfall}
          label="Budgeted-but-uncollected revenue"
          note={revenueNote}
        />
        <Row
          kind="headline"
          amount={headline}
          label="Funds Available (budgetary control)"
          note="The authoritative Mobius number that actually gates spending — and ties to Oracle."
        />
      </div>

      <p className="mt-3 text-xs leading-snug text-theme-secondary border-t border-theme-subtle/50 pt-3">
        Bottom line:{" "}
        <span className="font-semibold text-theme-primary tabular-nums">
          {usd0(headline)}
        </span>{" "}
        is the controlling &ldquo;what we can still spend&rdquo; figure.{" "}
        {gapMaterial ? (
          <>
            The{" "}
            <span className="font-semibold tabular-nums">
              {usd0(spendingSide)}
            </span>{" "}
            on the by-object report is gross budget-remaining that overstates it
            by{" "}
            <span className="tabular-nums">
              {usd0(spendingSide - headline)}
            </span>{" "}
            — it hasn&rsquo;t yet subtracted anticipated/capital adjustments or
            the revenue we budgeted but haven&rsquo;t collected.
          </>
        ) : (
          <>
            A raw by-object &ldquo;Funds Available&rdquo; export reads higher (
            <span className="tabular-nums">{usd0(bceaAvailable)}</span>) because
            it stops at the expenditure budget and doesn&rsquo;t net the{" "}
            <span className="tabular-nums">{usd0(revenueShortfall)}</span> of
            revenue we budgeted for but haven&rsquo;t collected.
          </>
        )}
      </p>

      {/* As-of stamp + tie-out disclosure: the headline is the Oracle snapshot;
          the decomposition is from the (sometimes earlier) BCEA recon pull. */}
      <p className="mt-2 text-[11px] leading-snug text-theme-muted">
        Funds Available as of{" "}
        <span className="tabular-nums">{snapAsOf ?? reconAsOf ?? "—"}</span>{" "}
        (ties to Oracle).
        {drift !== 0 && reconAsOf && reconAsOf !== snapAsOf ? (
          <>
            {" "}
            The breakdown above is from the {reconAsOf} reconciliation pull —{" "}
            <span className="tabular-nums">{usd0(Math.abs(drift))}</span> behind
            the current headline; the gap sits in the uncollected-revenue line
            and clears on the next pull.
          </>
        ) : null}
      </p>
    </section>
  );
}
